import React, { useMemo } from 'react';
import { Flex, Separator, Text, HStack } from '@skin-seoul/design-system';
import { CLASSNAME, Country, CountryCurrency, formatCurrency, getCouponDisplayName } from '@skin-seoul/service';
import type { CartCouponResponse } from '@skin-seoul/service';
import { Condition } from '@skin-seoul/utils';
import { LucideIcon } from '../../LucideIcon';
interface PriceCalculatorProps {
  subtotal: number;
  deliveryFee: number;
  totalPrice: number;
  totalDiscount: number;
  totalTax: number;
  coupons: CartCouponResponse[];
  isFreeShipping?: boolean;
  country: Country;
}
const CartOrderSummary = ({
  subtotal,
  deliveryFee,
  totalPrice,
  totalDiscount,
  totalTax,
  coupons,
  isFreeShipping = false,
  country
}: PriceCalculatorProps) => {
  const formattedSubtotal = formatCurrency(CountryCurrency[country], subtotal);
  const formattedDeliveryFee = formatCurrency(CountryCurrency[country], deliveryFee);
  const formattedTotalPrice = formatCurrency(CountryCurrency[country], totalPrice);
  const formattedTotalTax = formatCurrency(CountryCurrency[country], totalTax);

  // Transform coupon codes to display names (e.g., app20_skin_seoul -> APP20)
  const displayCoupons = useMemo(() => coupons.map(coupon => ({
    ...coupon,
    displayCode: getCouponDisplayName(coupon.code)
  })), [coupons]);
  return <Flex flexDirection="column" gap={2} data-sentry-element="Flex" data-sentry-component="CartOrderSummary" data-sentry-source-file="CartOrderSummary.tsx">
      <Flex flexDirection="column" gap={2} data-sentry-element="Flex" data-sentry-source-file="CartOrderSummary.tsx">
        <Flex alignItems="center" justifyContent="space-between" data-sentry-element="Flex" data-sentry-source-file="CartOrderSummary.tsx">
          <Text textStyle="paragraph.small" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="CartOrderSummary.tsx">
            Subtotal:
          </Text>
          <Text textStyle="paragraph.small" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="CartOrderSummary.tsx">
            {formattedSubtotal}
          </Text>
        </Flex>
        {displayCoupons.length > 0 && displayCoupons.map(coupon => <Flex alignItems="center" justifyContent="space-between" key={coupon.code}>
              <Text textStyle="paragraph.small" className={CLASSNAME.NOT_TRANSLATE}>
                {coupon.displayCode}:
              </Text>
              <Text textStyle="paragraph.small" className={CLASSNAME.NOT_TRANSLATE}>
                -{formatCurrency(CountryCurrency[country], coupon.discount)}
              </Text>
            </Flex>)}
        {/* Info message about highest percentage coupon */}
        <Condition expression={displayCoupons.length > 0} then={<HStack gap={1} alignItems="flex-start">
              <LucideIcon name="Info" boxSize={3} color="neutral.500" flexShrink={0} mt={0.5} />
              <Text textStyle="label.xsmall" color="neutral.500">
                Only the highest percentage coupon is applied.
              </Text>
            </HStack>} data-sentry-element="Condition" data-sentry-source-file="CartOrderSummary.tsx" />
        <Flex alignItems="center" justifyContent="space-between" data-sentry-element="Flex" data-sentry-source-file="CartOrderSummary.tsx">
          <Text textStyle="paragraph.small" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="CartOrderSummary.tsx">
            Delivery Fee:
          </Text>
          {isFreeShipping || deliveryFee === 0 ? <Text color="primary.600" textStyle="paragraph.medium" textTransform="uppercase" className={CLASSNAME.NOT_TRANSLATE}>
              Free
            </Text> : <Text textStyle="paragraph.small" className={CLASSNAME.NOT_TRANSLATE}>
              {formattedDeliveryFee}
            </Text>}
        </Flex>
        <Condition expression={country === Country.UnitedStates} then={<Flex alignItems="center" justifyContent="space-between">
              <Text textStyle="paragraph.small" className={CLASSNAME.NOT_TRANSLATE}>
                Customs & taxes:
              </Text>
              <Text textStyle="paragraph.small" className={CLASSNAME.NOT_TRANSLATE}>
                {formattedTotalTax}
              </Text>
            </Flex>} data-sentry-element="Condition" data-sentry-source-file="CartOrderSummary.tsx" />
      </Flex>
      <Separator data-sentry-element="Separator" data-sentry-source-file="CartOrderSummary.tsx" />
      <Flex alignItems="center" justifyContent="space-between" data-sentry-element="Flex" data-sentry-source-file="CartOrderSummary.tsx">
        <Text fontWeight="bold" textStyle="paragraph.large" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="CartOrderSummary.tsx">
          Order Total:
        </Text>
        <Text fontWeight="bold" textStyle="paragraph.large" className={CLASSNAME.NOT_TRANSLATE} data-sentry-element="Text" data-sentry-source-file="CartOrderSummary.tsx">
          {formattedTotalPrice}
        </Text>
      </Flex>
    </Flex>;
};
export default CartOrderSummary;